import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AlertTriangle } from 'lucide-react';
import { isUploadFileSaved, useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useAccount, usePerformLogout } from '@/hooks/data/account/useAccount';
import { TruncatedText } from '@/components/ui/truncated-text';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { Button } from '@/components/ui/button';
import { setEchoTokenGetter } from '@/lib/echo';
import { Loading } from '@/components/loading';
import { TaskState } from '@/types/uploads';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

export const AuthWall: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { profile, profileLoading } = useAccount();
  const { performLogout } = usePerformLogout();
  const { push } = useRouter();
  const { isPublicSharePage, forceLoginRequired } = useCurrentPage();
  const uploading = useUploadStore((state) => state.uploading);
  const authLostDuringUpload = useUploadStore((state) => state.authLostDuringUpload);
  const failInFlightUploads = useUploadStore((state) => state.failInFlightUploads);
  const tUpload = useTranslations('upload');

  // Subscribe to the stable slice and derive here: a selector that built objects would
  // return a fresh array every render and loop forever.
  const uploads = useUploadStore((state) => state.uploads);
  const authLostSessionIds = useUploadStore((state) => state.authLostSessionIds);
  const interruptedFiles = useMemo(
    () =>
      Object.entries(uploads)
        .filter(
          ([sessionId, session]) =>
            session.filesData.some((file) => !isUploadFileSaved(session, file.id)) &&
            (authLostSessionIds.includes(sessionId) || session.uploadState === TaskState.InProgress),
        )
        .flatMap(([, session]) =>
          session.filesData.map((file) => ({
            id: file.id,
            name: file.name,
            saved: isUploadFileSaved(session, file.id),
          })),
        ),
    [uploads, authLostSessionIds],
  );

  // Latched once auth is lost mid-upload. failInFlightUploads drains the queue, so
  // `uploading` flips back to false immediately — without the latch the effect
  // would re-fire and navigate away anyway, which is the behaviour being fixed.
  const [sessionEndedMidUpload, setSessionEndedMidUpload] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setEchoTokenGetter(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Navigating away unmounts the upload panel while the module-level upload loop
  // keeps running, so the user loses the batch and every record of what made it
  // through. When auth ends mid-upload, hold the page, stop the batch honestly and
  // let the user decide to sign in again (DM-2533).
  useEffect(() => {
    if ((!isPublicSharePage || forceLoginRequired) && !isAuthenticated && !isLoading) {
      if (sessionEndedMidUpload) {
        return;
      }
      // `uploading` covers auth flipping while the loop still runs; `authLostDuringUpload`
      // covers the commoner ordering where the failing token request ends the batch first.
      if (uploading || authLostDuringUpload) {
        setSessionEndedMidUpload(true);
        failInFlightUploads(tUpload('sessionEndedFileError'));
        return;
      }
      void push(`/identifier?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [
    isAuthenticated,
    isLoading,
    isPublicSharePage,
    forceLoginRequired,
    push,
    uploading,
    authLostDuringUpload,
    sessionEndedMidUpload,
    failInFlightUploads,
    tUpload,
  ]);

  // Rare case where the user is authenticated in auth0, but the auth0 domain does not match the one in the backend which returns a 401.
  // In this case, logging out the user should solve the issue
  useEffect(() => {
    if (isAuthenticated && !profileLoading && !profile) {
      void performLogout();
    }
  }, [profile, profileLoading, isAuthenticated, performLogout]);

  // The notice reports the batch outcome itself rather than relying on the upload panel in
  // the sidebar: without a profile, GuestWall renders only a spinner, so nothing below this
  // wall can display anything while the session is gone. Listing the files here is what
  // actually delivers "already-succeeded files stay visible as succeeded".
  if ((!isPublicSharePage || forceLoginRequired) && sessionEndedMidUpload) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-black dark:bg-neutral-950 dark:text-white">
        <div
          role="alert"
          className="flex max-h-[80vh] w-full max-w-lg flex-col items-center gap-5 rounded-lg bg-yellow-400/20 p-8 text-center dark:bg-yellow-500/10"
        >
          <AlertTriangle className="icon-12 stroke-yellow-400 dark:stroke-yellow-500" strokeWidth={1} />
          <p data-cy="upload-session-ended-message">{tUpload('sessionEnded')}</p>
          {interruptedFiles.length > 0 && (
            <>
              <p className="text-sm" data-cy="upload-session-ended-summary">
                {tUpload('sessionEndedSummary', {
                  saved: interruptedFiles.filter((file) => file.saved).length,
                  total: interruptedFiles.length,
                })}
              </p>
              <ul className="w-full overflow-y-auto text-left text-sm" data-cy="upload-session-ended-files">
                {interruptedFiles.map((file) => (
                  <li
                    key={file.id}
                    data-cy={`upload-session-ended-file-${file.saved ? 'saved' : 'failed'}`}
                    className="flex items-center justify-between gap-3 border-b border-black/10 py-1 last:border-0 dark:border-white/10"
                  >
                    <TruncatedText text={file.name} />
                    <span
                      className={file.saved ? 'text-green-700 dark:text-green-500' : 'text-red-700 dark:text-red-400'}
                    >
                      {file.saved ? tUpload('sessionEndedSaved') : tUpload('sessionEndedNotUploaded')}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button
            data-cy="upload-session-ended-sign-in"
            onClick={() => {
              void push(
                `/identifier?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`,
              );
            }}
          >
            {tUpload('sessionEndedSignIn')}
          </Button>
        </div>
      </div>
    );
  }

  if ((!isPublicSharePage || forceLoginRequired) && (isLoading || !isAuthenticated)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-neutral-950">
        <Loading />
      </div>
    );
  }

  return children;
};
